import { Alert, List, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatDateTime } from '../../lib/format';

/**
 * WHAT LAST NIGHT'S RUN COULD NOT WORK OUT, on the screen the owner opens to see what he owes.
 *
 * The commission ledger is rebuilt unattended every night, and it has always counted the tables it
 * could not do anything with — a tab tagged to somebody nobody has put on a plan, a tag that
 * matches no partner at all, a table already on a payout list. That count went into a Worker log
 * line. Nobody has ever opened one.
 *
 * So a night that worked out four tables out of seven looked exactly like a night that worked out
 * four, and the three that earned nothing stayed earning nothing until a partner happened to ask.
 * Money quietly not accruing is the failure this part of the system keeps producing, and being
 * invisible is the whole of what makes it expensive: nobody goes looking for a row that was never
 * drawn.
 *
 * ## Why it is a note and not a screen
 *
 * There is nothing to do here but go and look. Every reason names a thing the owner fixes
 * somewhere else — a partner's plan, a tab's tag, a payout he already drew up — so a panel with
 * its own table and filters would be a second place to read the same sentence. Three lines above
 * the balances, on the tab he is already on, is where it gets read.
 *
 * It renders NOTHING on a night that left nothing out, which is most nights. A panel that is
 * always there saying "all clear" is a panel people stop seeing.
 */

const AUDIT_PATH = '/admin/v1/admin/audit-events';
const NIGHTLY_ACTION = 'referrals.accruals.recompute';

interface LeftOut {
  reason: string;
  tables: number;
  /** The sentence the run itself wrote. Not re-worded here — one vocabulary, one answer. */
  words: string;
}

interface NightlyRun {
  at: string;
  detail: { leftOut?: LeftOut[] } | null;
}

export function WhatTheNightlyLeftOut() {
  const locationId = useLocationId();
  /*
   * The LAST run for this bar, and only the last.
   *
   * One row a night, including the nights that left nothing out — which is what makes reading the
   * newest one an answer about now rather than about whenever the last bad night was. A warning
   * left standing over tables somebody has since put right is worse than no warning: it teaches
   * the owner that this panel is usually wrong.
   */
  const query = { action: NIGHTLY_ACTION, entityType: 'commissionPlan', entityId: locationId, limit: 1 };
  const runs = useQuery({
    queryKey: listKey(AUDIT_PATH, query),
    enabled: Boolean(locationId),
    queryFn: () =>
      rawApi<NightlyRun[]>(
        'GET',
        `${AUDIT_PATH}?action=${NIGHTLY_ACTION}&entityType=commissionPlan` +
          `&entityId=${encodeURIComponent(locationId ?? '')}&limit=1`,
      ),
  });

  const last = runs.data?.[0];
  const leftOut = last?.detail?.leftOut ?? [];
  if (runs.isLoading || !last || leftOut.length === 0) return null;

  const tables = leftOut.reduce((sum, l) => sum + l.tables, 0);

  return (
    <Alert
      variant="light"
      color="orange"
      mb="md"
      title={`${tables} ${tables === 1 ? 'table' : 'tables'} earned nobody anything`}
    >
      <Text size="sm">
        The commission was worked out again on {formatDateTime(last.at)} and these were left out. Nothing in
        what we owe counts them.
      </Text>
      <List size="sm" mt={6} spacing={2}>
        {leftOut.map((l) => (
          <List.Item key={l.reason}>
            {l.tables} {l.tables === 1 ? 'table' : 'tables'} — {l.words}
          </List.Item>
        ))}
      </List>
      {/*
        What to do about it, which is the only reason to print any of this. The box that fixes it
        is directly below, so it is named rather than linked — a link to the screen you are already
        on reads as a mistake.
      */}
      <Text size="sm" mt={6}>
        Put the tag or the plan right and work the nights out again below. Leave it and tonight&apos;s run
        will pick it up on its own.
      </Text>
    </Alert>
  );
}
